import { ButtonHTMLType } from 'antd/lib/button/button'
import classNames from 'classnames'
import React, { CSSProperties, forwardRef, ReactNode } from 'react'
import { Link, LinkProps } from 'react-router-dom'

import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined'
import { ButtonBase } from '@material-ui/core'
import { ButtonBaseProps } from '@material-ui/core/ButtonBase'
import { makeStyles } from '@material-ui/styles'

import { them } from '../../them'

const sizeHooks = {
  lg: {
    height: them['height-lg'],
    padding: them['padding-md'],
    borderRadius: them['border-radius-md'],
    fontSize: them['font-size-default-big']
  },
  md: {
    height: them['height-md'],
    padding: them['padding-md'],
    borderRadius: them['border-radius-md'],
    fontSize: them['font-size-default-default']
  },
  sm: {
    height: them['height-sm'],
    padding: them['padding-sm'],
    borderRadius: them['border-radius-sm'],
    fontSize: them['font-size-default-default']
  },
  xs: {
    height: them['height-xs'],
    padding: them['padding-sm'],
    borderRadius: them['border-radius-sm'],
    fontSize: them['font-size-small-big']
  }
}

type ColorDefaultKey = 'color-default-default' | 'color-primary-default' | 'color-warn-default' | 'color-danger-default' | 'color-success-default'

type ColorSecondarytKey = 'color-default-secondary' | 'color-primary-secondary' | 'color-warn-secondary' | 'color-danger-secondary' | 'color-success-secondary'

interface GenerateCssReturn {
  /** 'warn' | 'danger' | 'success' 三种类型按钮返回公共CSS */
  background: string;
  color: string;
  hover: {
    opacity?: number;
    background: string;
    color?: string;
  };
}

/**
 * 生成公共css
 * @param plain 是否是朴素按钮
 * @param type 按钮类型
 */
const generateCss = (plain: boolean, type: 'warn' | 'danger' | 'success'): GenerateCssReturn => {
  const color = them[`color-${type}-default` as ColorDefaultKey]

  return plain
    ? {
      background: them[`color-${type}-secondary` as ColorSecondarytKey],
      color: color,
      hover: {
        background: color,
        color: 'white'
      }
    }
    : {
      background: color,
      color: 'white',
      hover: {
        color: 'white',
        opacity: 0.8,
        background: color
      }
    }
}

/** react-router 跳转 */
const AdapterLinkNode = (props: LinkProps, ref: LinkProps['innerRef']): JSX.Element => <Link innerRef={ref} {...props} />

const AdapterLink = forwardRef(AdapterLinkNode)

type BeforeOnClick = () => (void | Promise<void> | boolean | Promise<boolean> | never)

export interface ButtonProps extends Omit<ButtonBaseProps, 'type'> {
  size?: 'lg' | 'md' | 'sm' | 'xs'; /** 按钮尺寸 */
  type?: 'default' | 'primary' | 'warn' | 'danger' | 'success'; /** 按钮类型 */
  pure?: boolean; /** 是否为纯色背景按钮，主要为type=primary时有效 */
  plain?: boolean; /** 是否朴素按钮 */
  loading?: boolean; /** 是否加载中 */
  block?: boolean; /** 将按钮宽度调整为其父宽度的选项 */
  link?: boolean; /** 是否链接，去除默认padding，高及行高为字体大小的1.5倍 */
  icon?: ReactNode; /** icon图标 */
  target?: string; /** 相当于 a 链接的 target 属性，href 存在时生效 */
  rel?: string;/** a标签里面的 rel属性 rel="noreferrer" target为_blank 默认添加该属性 */
  href?: string; /** 点击跳转的地址，指定此属性 button 的行为和 a 链接一致 */
  htmlType?: ButtonHTMLType; /** 设置 button 原生的 type 值，可选值请参考 HTML 标准 https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type */
  beforeOnClick?: BeforeOnClick | BeforeOnClick[]; /** 单击前执行的操作 逻辑，必须resolve全部返回true才会执行onClick */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any; /** 点击按钮时的回调 */
  to?: LinkProps['to']; /** 使用 react-router 跳转 */
  style?: CSSProperties; /** style内联样式 */
  eventType?: 'info' | 'warn' | 'error' | 'event'; /** 日志上报类型 */
  eventValue?: any; /** 日志上报的内容 */
  scKey?: string; /** 神策上报 KEY */
  scValue?: object; /** 神策上报 value */
}

export const Button = ({ className, children, size = 'md', type = 'default', pure, plain = false, disabled, loading, block, link, icon, target = '_self', rel, href, htmlType = 'button', beforeOnClick, onClick, to, style, eventType, eventValue, scKey, scValue = {}, ...props }: ButtonProps): JSX.Element => {
  /** 主色包含渐变及纯背景色 */
  const primaryBackground = pure ? them['color-primary-default'] : them['color-primary-gradient']

  /** 各类型css */
  const typeHooks = {
    default: {
      background: 'white',
      color: them['color-default-default'],
      border: plain ? 0 : `1px solid ${them['color-line']}`,
      hover: {
        color: them['color-primary-default'],
        borderColor: them['color-primary-secondary'],
        background: plain ? 'white' : them['color-primary-secondary'],
        textDecoration: plain ? 'underline' : 'none'
      }
    },
    primary: {
      background: plain ? them['color-primary-secondary'] : primaryBackground,
      color: plain ? them['color-primary-default'] : 'white',
      hover: plain
        ? {
          background: primaryBackground,
          color: 'white'
        }
        : {
          opacity: 0.8,
          color: 'white'
        }
    },
    warn: generateCss(plain, 'warn'),
    danger: generateCss(plain, 'danger'),
    success: generateCss(plain, 'success')
  }

  const { background, hover } = typeHooks[type]
  const { fontSize } = sizeHooks[size]
  const border = !link && type === 'default' ? { border: typeHooks[type].border } : {}
  const lineHeight = !link ? {} : { lineHeight: `calc(${fontSize} * 1.5)` }

  const classes = makeStyles({
    root: {
      ...border,
      ...lineHeight,
      background: link ? 'none' : background,
      color: link ? them[`color-${type}-default` as ColorDefaultKey] : typeHooks[type].color,
      fontSize,
      borderRadius: link ? 0 : sizeHooks[size].borderRadius,
      height: link ? `calc(${fontSize} * 1.5)` : sizeHooks[size].height,
      fontWeight: 'normal',
      padding: link ? 0 : `0 ${type === 'default' && !plain ? `calc(${sizeHooks[size].padding} - 1px)` : sizeHooks[size].padding}`,
      width: block ? '100%' : 'auto',
      '& svg': {
        marginRight: children === undefined ? 0 : `calc(${them['padding-xs']} / 2)`
      },
      '&:hover': link ? { opacity: 0.8 } : hover,
      '&.Mui-disabled': loading
        ? {
          background,
          color: typeHooks[type].color,
          opacity: 0.65
        }
        : {
          background: them['color-disabled-button'],
          color: 'white'
        }
    }
  })()

  let jumpProps: {component?: ReactNode; to?: ButtonProps['to']; href?: ButtonProps['href']; rel?: ButtonProps['rel']; target?: ButtonProps['target']} = to !== undefined ? { component: AdapterLink, to } : {}

  /** href 和 to 同时设置情况下，href优先级更高 */
  if (href !== undefined) {
    jumpProps = {
      component: 'a',
      href,
      rel: href.split('.').length > 1 ? 'noreferrer' : rel,
      /** 如果是外链，则在新标签页打开 */
      target: href.split('.').length > 1 ? '_blank' : target
    }
  }

  /**
   * 处理单击
   * @param event 鼠标单击事件对象
   */
  const handleOnClick: ButtonProps['onClick'] = async (event) => {
    event.persist()
    let checkExecute = beforeOnClick === undefined

    /** beforeOnClick 逻辑，必须resolve全部返回true才会执行onClick */
    if (beforeOnClick) {
      const goExecute = Array.isArray(beforeOnClick) ? beforeOnClick : [beforeOnClick]
      for (const func of goExecute) {
        checkExecute = !!await func()

        /** 中间有一个未通过，则中断 */
        if (!checkExecute) break
      }
    }

    /** 日志上报 */
    if (eventType) {
      switch (eventType) {
        case 'info':
          /** @ts-ignore */
          console.tInfo(eventValue)
          break
        case 'warn':
          /** @ts-ignore */
          console.tWarn(eventValue)
          break
        case 'error':
          /** @ts-ignore */
          console.tError(eventValue)
          break
        case 'event':
          /** @ts-ignore */
          console.tEvent(eventValue)
          break
      }
    }

    /** 神策上报 */
    if (scKey) {
      // eslint-disable-next-line no-unused-expressions
      window.sensors?.track(scKey, scValue)
    }

    /** 执行真正的onClick函数 */
    checkExecute && onClick && onClick(event)
  }

  return (
    <ButtonBase
      className={classNames(classes.root, className)}
      disabled={disabled || loading}
      type={htmlType}
      onClick={handleOnClick}
      style={style}
      data-size={size}
      data-type={type}
      data-pure={pure}
      data-plain={plain}
      data-disabled={disabled}
      data-loading={loading}
      data-block={block}
      data-link={link}
      {...jumpProps}
      {...props}
    >
      {loading ? <LoadingOutlined /> : icon}
      {children}
    </ButtonBase>
  )
}
